import React, {useEffect, useRef, useState} from 'react';
import type {
  NativeSyntheticEvent,
  NativeTouchEvent,
  TextInputKeyPressEventData,
} from 'react-native';
import {ActivityIndicator, Platform, Pressable, StyleSheet, View} from 'react-native';
import Geolocation from 'react-native-geolocation-service';
import {observer} from 'mobx-react';
import {useDebounce} from 'use-debounce';

import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import {GeocodedSuburb} from '../models/middleware';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';
import {suburbKey} from '../utils/address';
import {logger, serializeError} from '../utils/logger';
import {requestFineLocationAccessPermission} from '../utils/permissions/permissions';

import Button from './themed/Button';
import Text from './themed/Text';
import TextInput, {TextInputRef} from './ui-controls/form/TextInput';
import {fontFamilyStyle} from './ui-controls/Text';
import {Icon} from './Icon';

const PRIMARY = theme.colour.uiControls?.primary ?? '#0054A6';
const RESULTS_LIMIT = 8;

export type SuburbAutocompleteProps = {
  placeholder?: string;
  // Optional shorter placeholder used at narrow viewports where the input
  // shares its row with one or more icon buttons. Falls back to placeholder.
  placeholderMobile?: string;
  onSelectSuburb: (suburb: GeocodedSuburb) => void;
  initialValue?: string;
  showSearchButton?: boolean;
  showLocationButton?: boolean;
  testID?: string;
};

const SuburbAutocomplete = observer(
  ({
    placeholder = 'Enter your postcode or suburb',
    placeholderMobile = 'Postcode or suburb',
    onSelectSuburb,
    initialValue = '',
    showSearchButton = true,
    showLocationButton = true,
    testID = 'suburb-autocomplete',
  }: SuburbAutocompleteProps) => {
    const {shopSelectionStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const inputRef = useRef<TextInputRef>(null);

    const [searchText, setSearchText] = useState(initialValue);
    const [selectedLabel, setSelectedLabel] = useState(initialValue);
    const [debouncedSearchText] = useDebounce(searchText, 300);
    const [isSearching, setIsSearching] = useState(false);
    const [geolocationError, setGeolocationError] = useState<string>('');
    const [resultsOpen, setResultsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const isMountedRef = useRef(true);
    const blurCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
      () => () => {
        isMountedRef.current = false;
        if (blurCloseTimerRef.current) clearTimeout(blurCloseTimerRef.current);
      },
      [],
    );

    useEffect(() => {
      let cancelled = false;
      async function getSuburbList() {
        if (debouncedSearchText && debouncedSearchText.length >= 3) {
          setIsSearching(true);
          shopSelectionStore.clearSuburbList();
          await shopSelectionStore.loadSuburbList(debouncedSearchText);
          if (!cancelled) setIsSearching(false);
        }
      }
      getSuburbList();
      return () => {
        cancelled = true;
      };
    }, [debouncedSearchText, shopSelectionStore]);

    const handleTextChanged = (raw: string) => {
      const filtered = raw.replace(/[^0-9a-zA-Z- ']/gi, '');
      setSearchText(filtered);
      setSelectedLabel(filtered);
      setGeolocationError('');
      setHighlightedIndex(-1);
      if (filtered.length >= 3) {
        setIsSearching(true);
        setResultsOpen(true);
      } else {
        setIsSearching(false);
        setResultsOpen(false);
        shopSelectionStore.clearSuburbList();
      }
    };

    const handleClear = () => handleTextChanged('');

    const handleSelectSuburb = (suburb: GeocodedSuburb) => {
      const label = `${suburb.locality} ${suburb.administrative_area} ${suburb.postal_code}`;
      setSelectedLabel(label);
      setSearchText('');
      setResultsOpen(false);
      setHighlightedIndex(-1);
      shopSelectionStore.clearSuburbList();
      inputRef.current?.blur?.();
      onSelectSuburb(suburb);
    };

    const handleManualSearchPress = async () => {
      if (searchText.length < 3) {
        return;
      }
      setIsSearching(true);
      setResultsOpen(true);
      try {
        await shopSelectionStore.loadSuburbList(searchText);
      } finally {
        setIsSearching(false);
      }
    };

    const handleGeolocation = async () => {
      setGeolocationError('');
      setIsSearching(true);
      const authorised = await requestFineLocationAccessPermission();
      if (!authorised) {
        logger.warn('SuburbAutocomplete', 'Geolocation permission not granted');
        setIsSearching(false);
        setGeolocationError('Geolocation permission not granted');
        return;
      }
      Geolocation.getCurrentPosition(
        async ({coords}) => {
          try {
            const suburbs = await shopSelectionStore.reverseGeocode(
              coords.latitude,
              coords.longitude,
            );
            if (!isMountedRef.current) return;
            if (suburbs.length === 1) {
              shopSelectionStore.resetLoading();
              handleSelectSuburb(suburbs[0]);
            } else if (suburbs.length > 1) {
              setResultsOpen(true);
            }
            setIsSearching(false);
          } catch (err) {
            logger.error(
              'SuburbAutocomplete',
              'Error during reverse geocoding',
              serializeError(err),
            );
            if (!isMountedRef.current) return;
            setIsSearching(false);
            setGeolocationError('Something went wrong, please try again');
          }
        },
        error => {
          if (!isMountedRef.current) return;
          setIsSearching(false);
          setGeolocationError(error.message);
        },
        {enableHighAccuracy: false, timeout: 10000, maximumAge: 10000},
      );
    };

    const showResults =
      resultsOpen && (searchText.length >= 3 || shopSelectionStore.suburbList.length > 0);
    const suburbs = shopSelectionStore.suburbList.slice(0, RESULTS_LIMIT);

    // Clamp highlight when the result set shrinks (e.g. typing narrows matches).
    useEffect(() => {
      if (highlightedIndex >= suburbs.length) setHighlightedIndex(-1);
    }, [suburbs.length, highlightedIndex]);

    // Delayed-close so a tap on a result row registers before we hide the
    // dropdown. Without the delay, blur (on tap-away) hides the list before
    // the row's onPress fires.
    const handleInputBlur = () => {
      if (blurCloseTimerRef.current) clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        setResultsOpen(false);
      }, 150);
    };

    const handleInputFocus = () => {
      if (blurCloseTimerRef.current) clearTimeout(blurCloseTimerRef.current);
      if (searchText.length >= 3 || shopSelectionStore.suburbList.length > 0) {
        setResultsOpen(true);
      }
    };

    const handleKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      const key = e.nativeEvent.key;
      if (!showResults && suburbs.length === 0) return;
      if (key === 'ArrowDown') {
        e.preventDefault?.();
        setResultsOpen(true);
        setHighlightedIndex(prev => (prev + 1) % Math.max(suburbs.length, 1));
      } else if (key === 'ArrowUp') {
        e.preventDefault?.();
        setHighlightedIndex(prev => {
          const next = prev <= 0 ? suburbs.length - 1 : prev - 1;
          return next;
        });
      } else if (key === 'Enter') {
        if (highlightedIndex >= 0 && suburbs[highlightedIndex]) {
          e.preventDefault?.();
          handleSelectSuburb(suburbs[highlightedIndex]);
        }
      } else if (key === 'Escape') {
        setResultsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    const renderResultRow = ({item: suburb, index}: {item: GeocodedSuburb; index: number}) => {
      const isHighlighted = index === highlightedIndex;
      return (
        <Pressable
          // Prevent the input's blur-close timer from firing when the user
          // taps a result row (mousedown fires before the blur timeout
          // resolves; clearing here keeps the dropdown open until onPress
          // runs and we close it via handleSelectSuburb).
          onPressIn={(_e: NativeSyntheticEvent<NativeTouchEvent>) => {
            if (blurCloseTimerRef.current) clearTimeout(blurCloseTimerRef.current);
          }}
          onPress={() => handleSelectSuburb(suburb)}
          onHoverIn={() => setHighlightedIndex(index)}
          style={({pressed}) => [
            styles.resultRow,
            isHighlighted && styles.resultRowHighlighted,
            pressed && styles.resultRowPressed,
          ]}
          testID="suburb-list-item">
          <Text numberOfLines={1} ellipsizeMode="tail" style={styles.resultText}>
            {`${suburb.locality} ${suburb.administrative_area} ${suburb.postal_code}`}
          </Text>
          <Icon name="chevron-right" size={12} color="#9CA3AF" />
        </Pressable>
      );
    };

    return (
      <View style={styles.container} testID={testID}>
        <View style={styles.row}>
          <View style={styles.inputWrap}>
            <TextInput
              ref={inputRef}
              value={selectedLabel}
              onChangeText={handleTextChanged}
              onSubmitEditing={handleManualSearchPress}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyPress={Platform.OS === 'web' ? handleKeyPress : undefined}
              placeholder={isWebDesktopScreen ? placeholder : placeholderMobile}
              autoCapitalize="words"
              autoCorrect={false}
              spellCheck={false}
              maxLength={80}
              returnKeyType="search"
              renderErrorMessage={false}
              leftIcon={<Icon name="location-dot" color="#6B7280" size={16} />}
              rightIcon={
                selectedLabel ? (
                  <Pressable onPress={handleClear} testID="suburb-autocomplete-clear">
                    <Icon name="circle-xmark" color="#9CA3AF" size={14} />
                  </Pressable>
                ) : undefined
              }
              testID="suburb-autocomplete-input"
              containerStyle={styles.inputContainer}
              inputContainerStyle={styles.inputField}
              inputStyle={styles.inputText}
            />
          </View>
          {showLocationButton ? (
            <Pressable
              onPress={handleGeolocation}
              style={({pressed}) => [styles.iconButton, pressed && styles.iconButtonPressed]}
              accessibilityLabel="Use current location"
              testID="suburb-autocomplete-geolocation">
              <Icon name="location-crosshairs" color={PRIMARY} size={18} />
            </Pressable>
          ) : null}
          {showSearchButton ? (
            isWebDesktopScreen ? (
              <Button
                title="SEARCH"
                onPress={handleManualSearchPress}
                disabled={searchText.length < 3}
                containerStyle={styles.searchButtonContainer}
                buttonStyle={styles.searchButton}
                titleStyle={styles.searchButtonTitle}
                testID="suburb-autocomplete-search"
              />
            ) : (
              <Pressable
                onPress={handleManualSearchPress}
                disabled={searchText.length < 3}
                style={({pressed}) => [
                  styles.searchIconButton,
                  searchText.length < 3 && styles.searchIconButtonDisabled,
                  pressed && styles.iconButtonPressed,
                ]}
                accessibilityLabel="Search"
                testID="suburb-autocomplete-search">
                <Icon name="magnifying-glass" color="#FFFFFF" size={18} />
              </Pressable>
            )
          ) : null}
        </View>

        {!!geolocationError && (
          <Text style={styles.errorText} testID="suburb-autocomplete-error">
            {`Unable to retrieve current location: ${geolocationError}.`}
          </Text>
        )}

        {showResults ? (
          <View style={styles.resultsCard} testID="suburb-autocomplete-results">
            {isSearching || shopSelectionStore.suburbListLoading || shopSelectionStore.geocoding ? (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color={PRIMARY} />
                <Text style={styles.loadingText}>Searching...</Text>
              </View>
            ) : suburbs.length === 0 ? (
              <Text style={styles.emptyText}>
                No matching suburbs were found. Please try again.
              </Text>
            ) : (
              // Plain View + map (not FlatList) so this dropdown never nests a
              // VirtualizedList inside the parent ScrollView — that nesting
              // breaks tap forwarding on native and silently blocks row
              // selection. Capped at RESULTS_LIMIT (8) so no virtualization is
              // needed.
              <View>
                {suburbs.map((suburb, index) => (
                  <React.Fragment key={suburbKey(suburb)}>
                    {index > 0 ? <View style={styles.separator} /> : null}
                    {renderResultRow({item: suburb, index})}
                  </React.Fragment>
                ))}
              </View>
            )}
          </View>
        ) : null}
      </View>
    );
  },
);

export default SuburbAutocomplete;

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inputWrap: {
    flex: 1,
    minWidth: 0,
    // Clip the browser's default focus outline so it doesn't bleed past
    // the input wrapper into the adjacent geolocation / search buttons.
    overflow: 'hidden',
    borderRadius: 8,
  },
  inputContainer: {
    paddingHorizontal: 0,
    margin: 0,
  },
  inputField: {
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    borderBottomWidth: 0,
    // Reserve enough horizontal space so the inner <input> text has
    // breathing room on both sides of leftIcon (location pin) and the
    // optional rightIcon (clear X). Without paddingRight RNW lets the
    // text run flush to the wrapper edge when typing past the visible
    // width, producing the clipped overflow seen in QA.
    paddingLeft: 12,
    paddingRight: 12,
    height: 44,
  },
  inputText: {
    fontSize: 14,
    color: '#111827',
    // Inner <input> right-side breathing room so long values don't sit
    // flush against the rightIcon / wrapper edge.
    paddingRight: 8,
    // RNW maps `outlineWidth` to the CSS outline property — kill the
    // browser's default focus ring so it can't overflow the wrapper.
    outlineWidth: 0,
  },
  iconButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconButtonPressed: {
    opacity: 0.7,
  },
  searchButtonContainer: {
    height: 44,
  },
  searchButton: {
    height: 44,
    paddingHorizontal: 24,
    borderRadius: 8,
    backgroundColor: PRIMARY,
  },
  searchButtonTitle: {
    ...fontFamilyStyle,
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.5,
    color: '#FFFFFF',
  },
  // Mobile-only icon-button variant for SEARCH so the input has room to
  // breathe at narrow widths.
  searchIconButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: PRIMARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchIconButtonDisabled: {
    opacity: 0.4,
  },
  resultsCard: {
    marginTop: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingVertical: 4,
    maxHeight: 320,
  },
  resultRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  resultRowPressed: {
    backgroundColor: '#F9FAFB',
  },
  resultRowHighlighted: {
    backgroundColor: '#F3F4F6',
  },
  resultText: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
    marginRight: 8,
  },
  separator: {
    height: 1,
    backgroundColor: '#F3F4F6',
    marginHorizontal: 12,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 8,
  },
  loadingText: {
    fontSize: 14,
    color: '#6B7280',
  },
  emptyText: {
    fontSize: 14,
    color: '#6B7280',
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  errorText: {
    fontSize: 13,
    color: '#DC2626',
    marginTop: 8,
  },
});
